import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, MapPin, Camera, Mic, Lightbulb, Users, Truck, CheckCircle2, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

const OnLocation = () => {
  const navigate = useNavigate();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const handleViewWork = () => {
    navigate('/', { state: { scrollTo: 'featured-work' } });
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const element = document.getElementById('featured-work');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const equipment = [
    { icon: Camera, title: 'Cinema-Grade Cameras', description: 'Multi-camera setups with 4K+ resolution' },
    { icon: Mic, title: 'Professional Audio', description: 'Wireless mics, boom operators, and sound mixing' },
    { icon: Lightbulb, title: 'Full Lighting Package', description: 'LED panels, softboxes, and grip equipment' },
    { icon: Users, title: 'Production Crew', description: 'Experienced directors, DOPs, and technicians' },
    { icon: Truck, title: 'Mobile Production Unit', description: 'Complete setup delivered to any location' }
  ];

  const locations = [
    { region: 'Europe', cities: ['London', 'Paris', 'Berlin', 'Amsterdam', 'Barcelona'] },
    { region: 'Americas', cities: ['New York', 'Miami', 'Los Angeles', 'Toronto', 'São Paulo'] },
    { region: 'Asia Pacific', cities: ['Tokyo', 'Singapore', 'Sydney', 'Dubai', 'Mumbai'] },
    { region: 'Caribbean', cities: ['Trinidad', 'Barbados', 'Jamaica', 'St. Lucia', 'Grenada'] }
  ];

  const projects = [
    { type: 'Corporate Events', description: 'Conference coverage, keynote speeches, and panel discussions' },
    { type: 'Brand Campaigns', description: 'Commercial shoots, product launches, and advertising content' },
    { type: 'Documentaries', description: 'Location-based storytelling with cinematic production value' },
    { type: 'Music Videos', description: 'Creative concepts filmed at unique worldwide locations' },
    { type: 'Interviews', description: 'High-profile interviews filmed at subject locations' },
    { type: 'Live Events', description: 'Festivals, concerts, and cultural celebrations' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png" 
            alt="LTV Production" 
            className="h-8 w-auto cursor-pointer"
            style={{ filter: 'invert(1)' }}
            onClick={() => navigate('/')}
          />
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-6 bg-black text-white px-6 py-3 rounded-full">
            <MapPin className="h-5 w-5" />
            <span className="text-lg font-medium">Worldwide Coverage</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6">On-Location Production</h1>
          <p className="text-2xl text-gray-700 mb-4">
            Broadcast-Ready Content, Anywhere in the World
          </p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            We bring cameras, audio, lighting, and a professional crew to your location — creating premium content that looks like it was filmed in a studio, no matter where you are.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setBookingModalOpen(true)} className="bg-black hover:bg-gray-800 text-white text-lg px-8 py-6">
              Request a Quote
            </Button>
            <Button size="lg" onClick={handleViewWork} variant="outline" className="border-2 border-black hover:bg-black hover:text-white text-lg px-8 py-6">
              View Our Work
            </Button>
          </div>
        </div>
      </section>

      {/* Why On-Location */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Why Choose On-Location Production?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sometimes your story needs to be told where it happens. We bring studio-quality production to any environment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-black transition-colors duration-300">
              <CardHeader>
                <CardTitle className="text-2xl">Authentic Settings</CardTitle>
                <CardDescription className="text-base">
                  Film in environments that add context and authenticity to your story
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-black transition-colors duration-300">
              <CardHeader>
                <CardTitle className="text-2xl">Global Reach</CardTitle>
                <CardDescription className="text-base">
                  Our production team travels internationally to capture your vision
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-2 border-gray-200 hover:border-black transition-colors duration-300">
              <CardHeader>
                <CardTitle className="text-2xl">Full Production</CardTitle>
                <CardDescription className="text-base">
                  Complete crew, equipment, and post-production services included
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Equipment & Crew */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">What We Bring to Your Location</h2>
            <p className="text-xl text-gray-600">Professional equipment and experienced crew</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {equipment.slice(0, 3).map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-black rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {equipment.slice(3).map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-black rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                    <CardDescription className="text-base">{item.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Perfect For</h2>
            <p className="text-xl text-gray-600">Versatile production for any project type</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-black transition-colors duration-300">
                <h3 className="text-xl font-bold mb-3">{project.type}</h3>
                <p className="text-gray-600">{project.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Worldwide Coverage */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Worldwide Coverage</h2>
            <p className="text-xl text-gray-300">We've filmed in over 40 countries across 6 continents</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {locations.map((location, index) => (
              <div key={index}>
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <MapPin className="h-6 w-6" />
                  {location.region}
                </h3>
                <ul className="space-y-2 text-gray-300">
                  {location.cities.map((city, cityIndex) => (
                    <li key={cityIndex} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      {city}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 mt-12 text-lg">
            Don't see your location? We travel anywhere — just ask.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">How It Works</h2>
            <p className="text-xl text-gray-600">Simple process from concept to delivery</p>
          </div>
          <div className="space-y-8">
            {[
              { step: '01', title: 'Discovery Call', description: 'Discuss your project, location, and creative vision' },
              { step: '02', title: 'Location Scout & Planning', description: 'We assess the location and plan logistics, permits, and crew' },
              { step: '03', title: 'Production Day(s)', description: 'Our team arrives with all equipment and captures your content' },
              { step: '04', title: 'Post-Production', description: 'Professional editing, color grading, and sound design' },
              { step: '05', title: 'Final Delivery', description: 'Receive your content optimized for all platforms' }
            ].map((item, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="text-6xl font-bold text-gray-200 flex-shrink-0 w-24">{item.step}</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-lg text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8">
            Ready to Film at Your Location?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Let's discuss your project and create a custom production plan for your location.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setBookingModalOpen(true)} className="bg-white text-black hover:bg-gray-200 text-lg px-8 py-6">
              <Phone className="mr-2 h-5 w-5" />
              Book a Call
            </Button>
            <Button size="lg" onClick={() => setBookingModalOpen(true)} variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6">
              <Mail className="mr-2 h-5 w-5" />
              Request a Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
};

export default OnLocation;